/**
 * UserManagement object represents user management-related API endpoints and their methods.
 */
const UserManagement = {
  /**
   * Endpoint for retrieving all users.
   * @memberof UserManagement
   * @type {object}
   * @property {string} url - The URL for retrieving all users.
   * @property {string} method - The HTTP method for retrieving all users (GET).
   */
  getUsers: {
    url: "/admin/user",
    method: "GET",
  },

  /**
   * Endpoint for retrieving a specific user by ID.
   * @memberof UserManagement
   * @function getSingleUser
   * @param {string} id - The ID of the user to retrieve.
   * @returns {object} The endpoint object containing URL and method.
   */

  getSingleUser: (id) => ({
    url: `/admin/user/${id}`,
    method: "GET",
  }),

  /**
   * Endpoint for updating user status by ID.
   * @memberof UserManagement
   * @function updateUserStatus
   * @param {string} id - The ID of the user whose status needs to be updated.
   * @returns {object} The endpoint object containing URL and method.
   */

  updateUserStatus: (id, status) => ({
    url: `/admin/user/${id}/${status}`,
    method: "POST",
  }),

  /**
   * Endpoint for updating user detail by ID.
   * @memberof UserManagement
   * @function updateUserDetail
   * @param {string} id - The ID of the user whose detail needs to be updated.
   * @returns {object} The endpoint object containing URL and method.
   */

  updateUserDetail: (id) => ({
    url: `/user/${id}`,
    method: "PUT",
  }),

  /**
   * Endpoint for deleting a user by ID.
   * @memberof UserManagement
   * @function deleteUserById
   * @param {string} id - The ID of the user whose detail needs to be delete.
   * @returns {object} The endpoint object containing URL and method.
   */

  deleteUserById: (id) => ({
    url: `/user/${id}`,
    method: "DELETE",
  }),

  /**
   * Endpoint for retrieving user nutrition data.
   * @memberof UserManagement
   * @type {object}
   * @property {string} url - The URL for retrieving user nutrition data.
   * @property {string} method - The HTTP method for retrieving user nutrition data (GET).
   */
  getUserNutrition: (id) => ({
    url: `/admin/user/nutrition-logs/${id}`,
    method: "GET",
  }),

  /**
   * Endpoint for retrieving user nutrition data by date.
   * @memberof UserManagement
   * @type {object}
   * @property {string} url - The URL for retrieving user nutrition data by date.
   * @property {string} method - The HTTP method for retrieving user nutrition data by date (GET).
   */
  getUserNutritionDetail: ({userId,id}) => ({
    url: `/admin/user/nutrition-logs/${userId}/${id}`,
    method: "GET",
  })
};

// Export the UserManagement object for use in other modules
export default UserManagement;
